import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  afterNextRender,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  fragmentFromUrl,
  observeScrollSections,
  scrollToUrlFragment,
} from '../../../../utils/platform-scroll.util';
import { pathFromUrl } from '../../../../../utils/platform-route.util';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormSaverComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { viewLoader } from '../../../../../../assets/animations/animations';
import { PlatformSettings } from '../../../../models/platform.models';
import { PlatformPauseDuration } from '../../../../enums/platform-pause-duration.enum';
import { PlatformRecommendationMode } from '../../../../enums/platform-recommendation-mode.enum';
import { PlatformSettingsService } from '../../../../services/platform-settings.service';
import { PlatformSettingsDeleteComponent } from '../../components/settings-delete/platform-settings-delete.component';
import { PlatformSettingsNotificationsComponent } from '../../components/settings-notifications/platform-settings-notifications.component';
import { PlatformSettingsPasswordComponent } from '../../components/settings-password/platform-settings-password.component';
import { PlatformSettingsTfaComponent } from '../../components/settings-tfa/platform-settings-tfa.component';
import { PlatformSettingsVisibilityComponent } from '../../components/settings-visibility/platform-settings-visibility.component';
import { PlatformSectionSpyService } from '../../../../services/platform-section-spy.service';

@Component({
  selector: 'app-platform-settings-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    FormSaverComponent,
    PlatformSettingsPasswordComponent,
    PlatformSettingsTfaComponent,
    PlatformSettingsVisibilityComponent,
    PlatformSettingsNotificationsComponent,
    PlatformSettingsDeleteComponent,
  ],
  templateUrl: './platform-settings-view.component.html',
  styleUrl: './platform-settings-view.component.scss',
  animations: [viewLoader],
})
export class PlatformSettingsViewComponent implements OnInit, AfterViewInit {
  private readonly fb = inject(FormBuilder);
  readonly settings = inject(PlatformSettingsService);
  private readonly toastsService = inject(ToastsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sectionSpy = inject(PlatformSectionSpyService);

  readonly formSaver = viewChild(FormSaverComponent);
  private readonly scrollRoot = viewChild<ElementRef<HTMLElement>>('scrollRoot');
  readonly saving = signal(false);

  private readonly sectionIds = [
    'visibility',
    'notifications',
    'password',
    'tfa',
    'delete',
  ] as const;

  readonly settingsForm: FormGroup = this.fb.group({
    recommendationMode: this.fb.control(PlatformRecommendationMode.RECEIVE),
    pauseDuration: this.fb.control(PlatformPauseDuration.SIX_MONTHS),
    notifyTalentpoolInvitation: this.fb.control(true),
    notifyJobRecommendation: this.fb.control(true),
    notifyApplicationStatus: this.fb.control(true),
  });

  constructor() {
    this.applySettings(this.settings.settings());
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => scrollToUrlFragment(this.router.url));
    afterNextRender(() => this.bindSectionSpy());
  }

  ngOnInit(): void {
    this.applySettings(this.settings.settings());
  }

  ngAfterViewInit(): void {
    this.applySettings(this.settings.settings());
    this.formSaver()?.updateOriginalValue();
    scrollToUrlFragment(this.router.url);
  }

  /**
   * Tracks the visible settings section for the sidenav and URL hash.
   */
  private bindSectionSpy(): void {
    const container = this.scrollRoot()?.nativeElement;
    if (!container) {
      return;
    }
    const path = pathFromUrl(this.router.url);
    const fragment = fragmentFromUrl(this.router.url);
    if (fragment) {
      this.sectionSpy.setSection(path, fragment, { syncUrl: false });
    }
    const stop = observeScrollSections(
      container,
      this.sectionIds,
      (sectionId) => {
        this.sectionSpy.setSection(path, sectionId);
      },
      { emitInitial: false }
    );
    this.destroyRef.onDestroy(stop);
  }

  /**
   * Persists visibility and notification settings after the API responds.
   */
  onSave(): void {
    this.saving.set(true);
    const next = this.settings.withPauseDates(
      this.formToSettings(),
      this.settings.settings()
    );
    this.settings.save(next).subscribe({
      next: () => {
        this.applySettings(next);
        this.saving.set(false);
        this.formSaver()?.updateOriginalValue();
        this.toastsService.success('jobboard.platform.settings.saved');
      },
      error: () => {
        this.saving.set(false);
        this.formSaver()?.shake();
        this.toastsService.warning('jobboard.platform.settings.save-error');
      },
    });
  }

  /**
   * Current settings used for pause-date display.
   * @returns Stored settings
   */
  savedSettings(): PlatformSettings {
    return this.settings.settings();
  }

  /**
   * Maps stored settings onto the form.
   * @param settings - Settings to apply
   */
  private applySettings(settings: PlatformSettings): void {
    this.settingsForm.patchValue({
      recommendationMode: settings.recommendationMode,
      pauseDuration: settings.pauseDuration,
      notifyTalentpoolInvitation: settings.notifyTalentpoolInvitation,
      notifyJobRecommendation: settings.notifyJobRecommendation,
      notifyApplicationStatus: settings.notifyApplicationStatus,
    });
  }

  /**
   * Reads settings from the form without pause timestamps.
   * @returns Form values for persistence
   */
  private formToSettings(): Omit<PlatformSettings, 'pausedUntil' | 'pausedAt'> {
    const value = this.settingsForm.getRawValue();
    return {
      recommendationMode: value.recommendationMode,
      pauseDuration: value.pauseDuration,
      notifyTalentpoolInvitation: value.notifyTalentpoolInvitation,
      notifyJobRecommendation: value.notifyJobRecommendation,
      notifyApplicationStatus: value.notifyApplicationStatus,
    };
  }
}
